import React, { useMemo, useState } from 'react';
import {
  Stack, Group, Title, Text, SegmentedControl, Paper, SimpleGrid,
  Loader, Drawer, Table, ScrollArea, Badge,
} from '@mantine/core';
import { Sankey, Tooltip, Rectangle, Layer, ResponsiveContainer } from 'recharts';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import DraftBadge from '../../common/DraftBadge';

// Disposition colors for the rotabull deal → enrichment flow.
const DISP_COLORS = {
  pending:           '#adb5bd', // gray — not yet enriched
  quote_now:         '#2f9e44', // green
  quote_with_review: '#12b886', // teal
  hold_for_sourcing: '#f08c00', // orange
  request_more_info: '#1971c2', // blue
  deprioritize:      '#495057', // dark gray
  incomplete:        '#e03131', // red — enriched, no action (partial/failed)
};
const DISP_BADGE = {
  pending: 'gray', quote_now: 'green', quote_with_review: 'teal',
  hold_for_sourcing: 'orange', request_more_info: 'blue', deprioritize: 'dark', incomplete: 'red',
};
const GROUP_COLOR = '#7048e8';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function trunc(s, n) { return s && s.length > n ? s.slice(0, n - 1) + '…' : s; }

function KpiCard({ label, value, color }) {
  return (
    <Paper withBorder p="md" radius="md">
      <Title order={2} c={color}>{value}</Title>
      <Text size="sm" c="dimmed">{label}</Text>
    </Paper>
  );
}

function SankeyNode(props) {
  const { x, y, width, height, payload } = props;
  const isGroup = payload.kind === 'group';
  const fill = isGroup ? GROUP_COLOR : (DISP_COLORS[payload.key] || '#868e96');
  return (
    <Layer>
      <Rectangle x={x} y={y} width={width} height={height} fill={fill} fillOpacity={0.9} />
      <text
        x={isGroup ? x - 6 : x + width + 6}
        y={y + height / 2}
        textAnchor={isGroup ? 'end' : 'start'}
        dominantBaseline="central"
        fontSize={11}
        fill="var(--mantine-color-text)"
      >
        {trunc(payload.name, isGroup ? 22 : 30)}
      </text>
    </Layer>
  );
}

function SankeyLinkEl(props) {
  const { sourceX, sourceY, sourceControlX, targetControlX, targetX, targetY, linkWidth, payload, nodes } = props;
  const ti = typeof payload.target === 'number' ? payload.target : payload.target?.index;
  const key = nodes?.[ti]?.key;
  const color = DISP_COLORS[key] || '#adb5bd';
  return (
    <path
      d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
      fill="none"
      stroke={color}
      strokeOpacity={0.3}
      strokeWidth={Math.max(1, linkWidth)}
    />
  );
}

export default function RotabullDeals() {
  const navigate = useNavigate();
  const [groupBy, setGroupBy] = useState('source');
  const [days, setDays] = useState('3');
  const [drill, setDrill] = useState(null);

  const flowUrl = useMemo(() => {
    const p = new URLSearchParams();
    p.set('groupBy', groupBy);
    p.set('days', days);
    p.set('topN', '12');
    return `${config.apiBaseUrl}/api/vesper/deal-flow?${p.toString()}`;
  }, [groupBy, days]);
  const { data, isLoading, error } = useAuthenticatedData(flowUrl);

  const tiles = data?.tiles;
  const sankey = data?.sankey;
  const groups = data?.groups ?? [];
  const nodes = sankey?.nodes ?? [];
  const hasSankey = sankey && Array.isArray(sankey.links) && sankey.links.length > 0;

  const drillUrl = useMemo(() => {
    if (!drill) return null;
    const p = new URLSearchParams();
    p.set('groupBy', groupBy);
    p.set('group', drill);
    p.set('days', days);
    p.set('take', '300');
    return `${config.apiBaseUrl}/api/vesper/deal-flow/drill?${p.toString()}`;
  }, [drill, groupBy, days]);
  const { data: drillRows } = useAuthenticatedData(drillUrl);

  const groupLabel = groupBy === 'segment' ? 'Segment' : groupBy === 'priority' ? 'Priority' : 'Source';
  const columns = useMemo(() => [
    { accessorKey: 'group', header: groupLabel },
    { accessorKey: 'deals', header: 'Deals' },
    { accessorKey: 'pending', header: 'Pending' },
    { accessorKey: 'enriched', header: 'Enriched' },
    { accessorKey: 'quoteReady', header: 'Quote-ready' },
    { accessorKey: 'incomplete', header: 'Incomplete' },
  ], [groupLabel]);

  const table = useMantineReactTable({
    columns,
    data: groups,
    enableRowSelection: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enablePagination: groups.length > 15,
    initialState: { density: 'xs' },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => setDrill(row.original.group),
      style: { cursor: 'pointer' },
    }),
  });

  return (
    <Stack gap="md" p="md">
      <DraftBadge />
      <Group justify="space-between" align="center">
        <Title order={2}>Rotabull Deals</Title>
        <Group gap="sm">
          <SegmentedControl size="xs" value={groupBy} onChange={setGroupBy}
            data={[{ label: 'Source', value: 'source' }, { label: 'Priority', value: 'priority' }, { label: 'Segment', value: 'segment' }]} />
          <SegmentedControl size="xs" value={days} onChange={setDays}
            data={[{ label: '3d', value: '3' }, { label: '7d', value: '7' }, { label: '14d', value: '14' }, { label: '30d', value: '30' }]} />
        </Group>
      </Group>

      {isLoading && <Stack align="center" mt="xl"><Loader /><Text c="dimmed">Loading deal flow…</Text></Stack>}
      {error && <Text c="red">Failed to load deal flow. Is the API reachable?</Text>}

      {!isLoading && !error && tiles && (
        <>
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }}>
            <KpiCard label="Total deals" value={tiles.total} />
            <KpiCard label="Enriched" value={`${tiles.enriched} (${tiles.enrichedPct}%)`} color="teal" />
            <KpiCard label="Pending enrichment" value={tiles.pending} color={tiles.pending > 0 ? 'gray' : undefined} />
            <KpiCard label="Quote-ready" value={tiles.quoteReady} color="green" />
            <KpiCard label="Incomplete" value={tiles.incomplete} color={tiles.incomplete > 0 ? 'red' : undefined} />
          </SimpleGrid>

          <Paper withBorder radius="md" p="md">
            <Text fw={600} mb="xs">Deal flow — where they came from → enrichment outcome</Text>
            {hasSankey ? (
              <ResponsiveContainer width="100%" height={Math.max(360, nodes.length * 22)}>
                <Sankey
                  data={sankey}
                  nodePadding={22}
                  nodeWidth={12}
                  margin={{ left: 140, right: 160, top: 12, bottom: 12 }}
                  node={<SankeyNode />}
                  link={<SankeyLinkEl nodes={nodes} />}
                >
                  <Tooltip />
                </Sankey>
              </ResponsiveContainer>
            ) : (
              <Text c="dimmed">No deals in this window.</Text>
            )}
          </Paper>

          <Paper withBorder radius="md">
            <MantineReactTable table={table} />
          </Paper>
        </>
      )}

      <Drawer opened={!!drill} onClose={() => setDrill(null)} position="right" size="xl"
        title={<Text fw={600}>Deals — {drill}</Text>}>
        <ScrollArea>
          <Table striped highlightOnHover fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Created</Table.Th>
                <Table.Th>Customer</Table.Th>
                <Table.Th>Priority</Table.Th>
                <Table.Th>Score</Table.Th>
                <Table.Th>Disposition</Table.Th>
                <Table.Th>In stock</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(drillRows ?? []).map((r) => (
                <Table.Tr key={r.deal_public_id} style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/sales/funnel/deal/${encodeURIComponent(r.deal_public_id)}`)}>
                  <Table.Td>{fmtDate(r.rfq_create_date)}</Table.Td>
                  <Table.Td><Text size="sm">{trunc(r.buyer_company_name, 40) || '—'}</Text></Table.Td>
                  <Table.Td>{r.priority || '—'}</Table.Td>
                  <Table.Td>{r.priority_score ?? '—'}</Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={DISP_BADGE[r.disposition] || 'gray'} variant="light">
                      {r.disposition}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{r.has_part_in_stock ? 'Y' : ''}</Table.Td>
                </Table.Tr>
              ))}
              {(!drillRows || drillRows.length === 0) && (
                <Table.Tr><Table.Td colSpan={6}><Text c="dimmed" ta="center" py="md">No deals.</Text></Table.Td></Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Drawer>
    </Stack>
  );
}
